import React, { useEffect, useState } from "react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import "./Users.css";
import menuPermissions, { permissionDescriptions } from "../../config/menuPermissions";

const PRELAUNCH_ACCESS = "portal.prelaunch_access";

const Users = () => {
  const { t } = useLanguage();
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", password: "", role_id: "", role_ids: [], branch_id: "", status: "active" });
  const [roleForm, setRoleForm] = useState({
    name: "",
    slug: "",
    description: "",
    permissions: ["dashboard.view", "branches.options"],
  });
  const [permissionUser, setPermissionUser] = useState(null);
  const [permissionOverrides, setPermissionOverrides] = useState({});
  const [permissionRoleDefaults, setPermissionRoleDefaults] = useState([]);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [permissionSuperAdmin, setPermissionSuperAdmin] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [userResponse, roleResponse, branchResponse] = await Promise.all([
        api.getUsers(),
        api.getRoles(),
        api.getBranchOptions(),
      ]);
      setUsers(userResponse.data || []);
      setRoles(roleResponse.data || []);
      setPermissionOptions(roleResponse.permission_options || []);
      setBranches(branchResponse.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadData();
  }, [isAdmin]);

  const resetUserForm = () => {
    setEditingUserId(null);
    setForm({ email: "", password: "", role_id: "", role_ids: [], branch_id: "", status: "active" });
  };

  const handleSaveUser = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      if (editingUserId) {
        const payload = { email: form.email, role_id: form.role_id, role_ids: form.role_ids, branch_id: form.branch_id, status: form.status };
        if (form.password) payload.password = form.password;
        await api.updateUser(editingUserId, payload);
      } else {
        await api.createUser(form);
      }
      setMessage(editingUserId ? "User updated successfully." : "User created successfully.");
      resetUserForm();
      await loadData();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const editUser = (user) => {
    setEditingUserId(user.id);
    setForm({
      email: user.email,
      password: "",
      role_id: String(user.role_id),
      role_ids: (user.role_ids || []).map(String),
      branch_id: user.branch_id || "",
      status: user.status || "active",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openPermissions = async (user) => {
    setPermissionUser(user);
    setPermissionLoading(true);
    setError("");
    try {
      const response = await api.getUserPermissions(user.id);
      const data = response.data || {};
      setPermissionOptions(data.permission_options || permissionOptions);
      setPermissionRoleDefaults(data.role_permissions || []);
      setPermissionSuperAdmin(Boolean(data.is_super_admin));
      setPermissionOverrides(
        (data.overrides || []).reduce(
          (result, item) => ({ ...result, [item.permission]: item.effect }),
          {},
        ),
      );
    } catch (e) {
      setError(e.message);
      setPermissionUser(null);
    } finally {
      setPermissionLoading(false);
    }
  };

  // Pilot testers may use the portal while it is in pre-launch mode; stored as a
  // per-user "allow" override so the rest of the user's overrides are preserved.
  const togglePilotTester = async (user) => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const response = await api.getUserPermissions(user.id);
      const overrides = (response.data?.overrides || []).filter((item) => item.permission !== PRELAUNCH_ACCESS);
      if (!user.prelaunch_access) overrides.push({ permission: PRELAUNCH_ACCESS, effect: "allow" });
      await api.updateUserPermissions(user.id, overrides);
      setMessage(user.prelaunch_access
        ? `${user.email} is no longer a pilot tester and will see Coming Soon while the portal is in pre-launch mode.`
        : `${user.email} is now a pilot tester and can use the full portal during pre-launch.`);
      await loadData();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const savePermissions = async () => {
    if (!permissionUser) return;
    setSaving(true);
    setError("");
    try {
      const overrides = Object.entries(permissionOverrides)
        .filter(([, effect]) => effect === "allow" || effect === "deny")
        .map(([permission, effect]) => ({ permission, effect }));
      await api.updateUserPermissions(permissionUser.id, overrides);
      setMessage(`Permissions for ${permissionUser.email} updated successfully.`);
      setPermissionUser(null);
      loadData();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const permissionGroups = permissionOptions.reduce((groups, permission) => {
    const domain = permission.split(".")[0];
    if (!groups[domain]) groups[domain] = [];
    groups[domain].push(permission);
    return groups;
  }, {});

  const togglePermission = (permission) =>
    setRoleForm((current) => ({
      ...current,
      permissions: current.permissions.includes(permission)
        ? current.permissions.filter((item) => item !== permission)
        : [...current.permissions, permission],
    }));

  const handleCreateRole = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const response = await api.createRole(roleForm);
      const newRole = response.data;
      setRoleForm({
        name: "",
        slug: "",
        description: "",
        permissions: ["dashboard.view", "branches.options"],
      });
      setShowRoleForm(false);
      setMessage("Role created. It is now available in the dropdown.");
      await loadData();
      if (newRole?.id)
        setForm((current) => ({ ...current, role_id: String(newRole.id) }));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin)
    return (
      <div className="card">
        <p>{t("Access denied")}</p>
      </div>
    );

  return (
    <div className="users-page">
      <div className="users-heading">
        <div>
          <span>ADMINISTRATION</span>
          <h1>{t("Users")}</h1>
          <p>Create system users and assign access roles.</p>
        </div>
      </div>
      {error && (
        <div className="users-message error">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}
      {message && (
        <div className="users-message success">
          {message}
          <button onClick={() => setMessage("")}>×</button>
        </div>
      )}

      <div className="users-grid">
        <div className="card user-form-card">
          <div className="card-title-row">
            <div>
              <h2>{editingUserId ? "Edit User" : t("Create User")}</h2>
              <p>{editingUserId ? "Update account access, role or password." : "Choose an existing role or create a new one."}</p>
            </div>
            <button
              className="secondary-button"
              type="button"
              onClick={() => setShowRoleForm(!showRoleForm)}
            >
              {showRoleForm ? "Close role form" : "+ New role"}
            </button>
          </div>
          <form onSubmit={handleSaveUser}>
            <div className="form-group">
              <label>{t("Email")}</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>{t("Password")}</label>
              <input
                type="password"
                minLength="8"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required={!editingUserId}
              />
              <small>{editingUserId ? "Leave blank to keep the current password." : "Minimum 8 characters."}</small>
            </div>
            <div className="form-group">
              <label>{t("Role")}</label>
              <select
                value={form.role_id}
                onChange={(e) => setForm({ ...form, role_id: e.target.value })}
                required
              >
                <option value="">Select role</option>
              {roles.filter((role) => role.slug !== "member" || (editingUserId && String(role.id) === String(form.role_id))).map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name} ({role.slug})
                  </option>
                ))}
              </select>
              <small>Primary role controls the user's main workspace.</small>
            </div>
            <div className="form-group">
              <label>Additional roles</label>
              <div className="permission-grid user-role-grid">
                {roles.map((role) => (
                  <label key={role.id}>
                    <input
                      type="checkbox"
                      disabled={String(role.id) === String(form.role_id)}
                      checked={String(role.id) === String(form.role_id) || form.role_ids.includes(String(role.id))}
                      onChange={() => setForm((current) => ({
                        ...current,
                        role_ids: current.role_ids.includes(String(role.id))
                          ? current.role_ids.filter((id) => id !== String(role.id))
                          : [...current.role_ids, String(role.id)],
                      }))}
                    />
                    <span>{role.name}</span>
                  </label>
                ))}
              </div>
              <small>For a union leader who is also a member, select Member here and choose the leadership role as Primary.</small>
            </div>
            <div className="form-group">
              <label>Branch</label>
              <select value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}>
                <option value="">No branch assigned</option>
                {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.code} — {branch.name}</option>)}
              </select>
              <small>This branch will be used by Staff Profile and requisitions.</small>
            </div>
            {editingUserId && (
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            )}
            <button className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : editingUserId ? "Save Changes" : t("Create User")}
            </button>
            {editingUserId && <button className="secondary-button cancel-user-edit" type="button" onClick={resetUserForm}>Cancel</button>}
          </form>

          {showRoleForm && (
            <form className="new-role-form" onSubmit={handleCreateRole}>
              <h3>Create a new role</h3>
              <div className="role-fields">
                <div className="form-group">
                  <label>Role name</label>
                  <input
                    required
                    value={roleForm.name}
                    onChange={(e) =>
                      setRoleForm({ ...roleForm, name: e.target.value })
                    }
                    placeholder="Example: Procurement Officer"
                  />
                </div>
                <div className="form-group">
                  <label>Role code</label>
                  <input
                    value={roleForm.slug}
                    onChange={(e) =>
                      setRoleForm({
                        ...roleForm,
                        slug: e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9_]/g, "_"),
                      })
                    }
                    placeholder="Generated automatically"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  value={roleForm.description}
                  onChange={(e) =>
                    setRoleForm({ ...roleForm, description: e.target.value })
                  }
                />
              </div>
              <label className="permissions-label">Permissions</label>
              <div className="permission-grid">
                {permissionOptions.map((permission) => (
                  <label key={permission}>
                    <input
                      type="checkbox"
                      checked={roleForm.permissions.includes(permission)}
                      onChange={() => togglePermission(permission)}
                    />{" "}
                    <span>{permission}</span>
                  </label>
                ))}
              </div>
              <button className="btn btn-primary" disabled={saving}>
                {saving ? "Saving…" : "Create role"}
              </button>
            </form>
          )}
        </div>

        <div className="card users-list-card">
          <div className="card-title-row">
            <div>
              <h2>System users</h2>
              <p>{users.length} registered users</p>
            </div>
          </div>
          {loading ? (
            <p>{t("Loading...")}</p>
          ) : (
            <div className="users-table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("ID")}</th>
                    <th>{t("Email")}</th>
                    <th>{t("Role")}</th>
                    <th>Branch</th>
                    <th>Status</th>
                    <th>Last login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.email}</td>
                      <td>
                        {(user.roles?.length ? user.roles : [{ name: user.role_name || `Role #${user.role_id}` }]).map((role) => <span className="role-badge" key={role.id || role.name}>{role.name}</span>)}
                        {user.prelaunch_access && <span className="role-badge pilot-badge" title="Can use the portal during pre-launch">Pilot tester</span>}
                      </td>
                      <td>{user.branch_name || "Not assigned"}</td>
                      <td>
                        <span className={`user-status ${user.status}`}>
                          {user.status}
                        </span>
                      </td>
                      <td>
                        {user.last_login
                          ? new Date(user.last_login).toLocaleString("en-TZ")
                          : "Never"}
                      </td>
                      <td><div className="user-row-actions"><button className="table-edit-button" type="button" onClick={() => editUser(user)}>Edit</button><button className="table-permission-button" type="button" onClick={() => openPermissions(user)}>Permissions</button>{!(user.roles || []).some((role) => role.slug === "admin") && <button className={`table-pilot-button ${user.prelaunch_access ? "on" : ""}`} type="button" disabled={saving} onClick={() => togglePilotTester(user)}>{user.prelaunch_access ? "Remove pilot" : "Make pilot"}</button>}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {permissionUser && (
        <div className="card user-permissions-card">
          <div className="card-title-row">
            <div>
              <span className="permissions-eyebrow">ACCESS CONTROL</span>
              <h2>User permissions</h2>
              <p>{permissionUser.email} · choose an exception to the assigned role permissions.</p>
            </div>
            <button className="secondary-button" type="button" onClick={() => setPermissionUser(null)}>Close</button>
          </div>
          {permissionLoading ? <p>{t("Loading...")}</p> : permissionSuperAdmin ? (
            <div className="permission-protected">This is a Super Admin account. Its full access is protected; assign a normal role before applying individual permissions.</div>
          ) : (
            <>
              <div className="permission-legend"><span><i className="role-dot" /> Role default</span><span><i className="allow-dot" /> Allow</span><span><i className="deny-dot" /> Deny — also hides the related menu for this user</span></div>
              <div className="permission-groups">
                {Object.entries(permissionGroups).map(([domain, permissions]) => (
                  <section key={domain} className="permission-group">
                    <h3>{domain.replaceAll("_", " ")}</h3>
                    {permissions.map((permission) => {
                      const inherited = permissionRoleDefaults.includes("*") || permissionRoleDefaults.includes(permission) || permissionRoleDefaults.includes(`${domain}.*`);
                      return (
                        <label key={permission}>
                          <span><b>{permission}</b><small>{permission === PRELAUNCH_ACCESS ? "Pilot tester — can use the portal while it is in pre-launch mode" : inherited ? "Allowed by role" : "Not included in role"}</small>{permissionDescriptions[permission] && <small className="permission-description">{permissionDescriptions[permission]}</small>}{menuPermissions[permission] && <small className={`permission-menu-tag ${permissionOverrides[permission] === "deny" ? "hidden-menu" : ""}`}>{permissionOverrides[permission] === "deny" ? "Menu hidden: " : "Menu: "}{menuPermissions[permission].join(", ")}</small>}</span>
                          <select value={permissionOverrides[permission] || "inherit"} onChange={(event) => setPermissionOverrides((current) => ({ ...current, [permission]: event.target.value }))}>
                            <option value="inherit">Role default</option>
                            <option value="allow">Allow</option>
                            <option value="deny">Deny</option>
                          </select>
                        </label>
                      );
                    })}
                  </section>
                ))}
              </div>
              <div className="permission-actions"><button className="secondary-button" type="button" onClick={() => setPermissionUser(null)}>Cancel</button><button className="btn btn-primary" type="button" disabled={saving} onClick={savePermissions}>{saving ? "Saving…" : "Save permissions"}</button></div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Users;
